import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BTLogo from "../assets/images/bt-logo.svg";
import { useAuth } from "../AuthContext";

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userInfo, logout } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleDropdown = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div id="header-div">
      <style>{`
        .transition-all {
          transition: all 0.2s ease-in-out;
        }
        .rotate-180 {
          transform: rotate(180deg);
        }
        .dropdown-menu.show {
          display: block;
          opacity: 1;
          visibility: visible;
        }
      `}</style>
      {isAuthenticated ? (
        <div className="d-flex justify-content-between align-items-center w-100 px-4" ref={headerRef}>
          <div id="header-div-logo">
            <Link to="/dashboard">
              <img src={BTLogo} height={40} alt="Better Trucks Logo" />
            </Link>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {/* Overview & Analytics */}
            <div className="dropdown position-relative">
              <button
                className={`btn text-white border-0 bg-transparent d-flex align-items-center ${['/dashboard', '/customers'].includes(location.pathname) ? 'fw-bold' : ''}`}
                type="button"
                onClick={() => toggleDropdown('overview')}
              >
                Overview & Analytics
                <i className={`bi bi-chevron-down ms-1 transition-all ${activeDropdown === 'overview' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'overview' && (
                <ul className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                  <li>
                    <Link
                      to="/dashboard"
                      className={`dropdown-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-speedometer2 me-2"></i>Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/customers"
                      className={`dropdown-item ${location.pathname === '/customers' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-building me-2"></i>Customers
                    </Link>
                  </li>
                </ul>
              )}
            </div>
            
            {/* Address Management & Data */}
            <div className="dropdown position-relative">
              <button
                className={`btn text-white border-0 bg-transparent d-flex align-items-center ${['/address-management', '/address-aliases', '/pods'].includes(location.pathname) ? 'fw-bold' : ''}`}
                type="button"
                onClick={() => toggleDropdown('address')}
              >
                Address & Data
                <i className={`bi bi-chevron-down ms-1 transition-all ${activeDropdown === 'address' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'address' && (
                <ul className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                  <li>
                    <Link
                      to="/address-management"
                      className={`dropdown-item ${location.pathname === '/address-management' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-gear me-2"></i>Address Management
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/address-aliases"
                      className={`dropdown-item ${location.pathname === '/address-aliases' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-signpost-split me-2"></i>Address Aliases
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/pods"
                      className={`dropdown-item ${location.pathname === '/pods' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-receipt me-2"></i>PODs
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* Reports */}
            <div className="dropdown position-relative">
              <button
                className={`btn text-white border-0 bg-transparent d-flex align-items-center ${['/reports/provider-disagreements'].includes(location.pathname) ? 'fw-bold' : ''}`}
                type="button"
                onClick={() => toggleDropdown('reports')}
              >
                Reports
                <i className={`bi bi-chevron-down ms-1 transition-all ${activeDropdown === 'reports' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'reports' && (
                <ul className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                  <li>
                    <Link
                      to="/reports/provider-disagreements"
                      className={`dropdown-item ${location.pathname === '/reports/provider-disagreements' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-pin-map me-2"></i>Provider Disagreements
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* Feedback & Monitoring */}
            <div className="dropdown position-relative">
              <button
                className={`btn text-white border-0 bg-transparent d-flex align-items-center ${['/addressValidateFeedback', '/geocodeReverseFeedback'].includes(location.pathname) ? 'fw-bold' : ''}`}
                type="button"
                onClick={() => toggleDropdown('monitoring')}
              >
                Monitoring
                <i className={`bi bi-chevron-down ms-1 transition-all ${activeDropdown === 'monitoring' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'monitoring' && (
                <ul className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                  <li>
                    <Link
                      to="/addressValidateFeedback"
                      className={`dropdown-item ${location.pathname === '/addressValidateFeedback' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-check-circle me-2"></i>Address Validate Feedback
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/geocodeReverseFeedback"
                      className={`dropdown-item ${location.pathname === '/geocodeReverseFeedback' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>Geocode Reverse Feedback
                    </Link>
                  </li> */}
                </ul>
              )}
            </div>

            {/* Development Tools */}
            <div className="dropdown position-relative">
              <button
                className={`btn text-white border-0 bg-transparent d-flex align-items-center ${location.pathname === '/debugging' ? 'fw-bold' : ''}`}
                type="button"
                onClick={() => toggleDropdown('tools')}
              >
                Tools
                <i className={`bi bi-chevron-down ms-1 transition-all ${activeDropdown === 'tools' ? 'rotate-180' : ''}`}></i>
              </button>
              {activeDropdown === 'tools' && (
                <ul className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                  <li>
                    <Link
                      to="/debugging"
                      className={`dropdown-item ${location.pathname === '/debugging' ? 'active' : ''}`}
                      onClick={closeDropdown}
                    >
                      <i className="bi bi-bug me-2"></i>Debugging
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '1rem',
              padding: '0.25rem 0.75rem',
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '4px',
              color: '#fff'
            }}>
              <i className="bi bi-person-circle me-2" style={{ fontSize: '14px' }}></i>
              <span style={{ fontWeight: 500, fontSize: '14px' }}>
                {userInfo?.email || 'User'}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="btn btn-outline-light btn-sm"
              style={{ fontWeight: 600 }}
            >
              <i className="bi bi-box-arrow-right me-1"></i>
              Logout
            </button>
          </div>
        </div>
      ) : (
        <div id="header-div-logo">
          <img src={BTLogo} height={40} alt="Better Trucks Logo" />
        </div>
      )}
    </div>
  );
};

export default Header;
